import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useToast } from "@bitsentry-ce/components/hooks/use-toast";
import { toSonnerAction, toSonnerDuration } from "./legacy-toast-compat";

type LegacyToast = ReturnType<typeof useToast>["toasts"][number];

// Shared components still use the legacy toast API. Render their notifications
// through the same Sonner host as the dashboard, without loading Radix Toast.
export default function LegacyToastBridge() {
  const { toasts } = useToast();
  const displayed = useRef(new Map<string, LegacyToast>());

  useEffect(() => {
    const activeIds = new Set<string>();
    for (const notification of toasts) {
      const id = `legacy:${notification.id}`;
      if (!notification.open) continue;
      activeIds.add(id);
      if (displayed.current.get(id) === notification) continue;
      displayed.current.set(id, notification);

      const show = notification.variant === "destructive" ? toast.error : toast;
      const close = () => notification.onOpenChange?.(false);
      show(notification.title, {
        id,
        description: notification.description,
        action: toSonnerAction(notification.action, close),
        duration: toSonnerDuration(notification.duration),
        closeButton: true,
        onDismiss: close,
        onAutoClose: close,
        className:
          notification.variant === "destructive"
            ? "!bg-destructive !text-destructive-foreground"
            : notification.className,
      });
    }

    for (const id of displayed.current.keys()) {
      if (activeIds.has(id)) continue;
      toast.dismiss(id);
      displayed.current.delete(id);
    }
  }, [toasts]);

  useEffect(() => {
    const notifications = displayed.current;
    return () => {
      for (const id of notifications.keys()) toast.dismiss(id);
      notifications.clear();
    };
  }, []);

  return null;
}
